import { useRuntimeConfig } from '#imports'
import { computed } from 'vue'

import { useShoppingAssistantWebSidebar } from '../stores/useShoppingAssistantWebSidebar'

import { useIsShoppingAssistantWebEnabled } from './useIsShoppingAssistantWebEnabled'
import { useShoppingAssistantViewport } from './useShoppingAssistantViewport'

/**
 * Double-search experiment: for a random percentage of users, pressing Enter
 * in the header search bar both navigates to the search results page AND seeds
 * a conversation in the assistant web sidebar.
 *
 * Gated by:
 * - `useIsShoppingAssistantWebEnabled` (assistant web feature must be on)
 * - `useShoppingAssistantViewport().isDesktop` (≥lg — panel + results visible side-by-side)
 * - `FF_SHOPPING_ASSISTANT_DOUBLE_SEARCH_PROBABILITY` > 0 (env-overridable, defaults to 0)
 * - Sidebar closed OR open with no user messages (don't interrupt engaged users)
 *
 * Randomization is absolute (per-submit, not sticky per-user) via `Math.random()`.
 */
export function useDoubleSearchExperiment() {
  const { public: config } = useRuntimeConfig()
  const isShoppingAssistantWebEnabled = useIsShoppingAssistantWebEnabled()
  const { isDesktop } = useShoppingAssistantViewport()
  const store = useShoppingAssistantWebSidebar()

  const probability = config.FF_SHOPPING_ASSISTANT_DOUBLE_SEARCH_PROBABILITY

  const isEnabled = computed(
    () =>
      isShoppingAssistantWebEnabled.value && isDesktop.value && probability > 0,
  )

  function shouldDoubleSearch(): boolean {
    if (!isEnabled.value) return false
    if (Math.random() >= probability) return false
    if (store.isOpen && store.hasUserMessages) return false

    return true
  }

  return { shouldDoubleSearch }
}
